// src/pages/settings/LlmAccountsPage.tsx
//
// Contas LLM — /settings/llm (Reforma UX §6.9, LLM-01..04).
// Extraída da antiga SettingsPage (aba `llm`): listagem de contas
// (provedor · método · modelo · status), seletor de modelo padrão da conta,
// lixeira (remover) e o modal único "Conectar LLM" que orquestra os 4 fluxos:
// OAuth Anthropic, OAuth OpenAI (device code), API Key e Reconnect.
//
// LLM-04: o callback OAuth volta pra /settings/llm?llm=connected — esta
// página processa o query param e mostra o feedback de sucesso/erro.

import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import type { LlmAccountWithStatus, ConnectionStatus, QuotaWindow } from '@/lib/api';
import {
  Cpu,
  Trash2,
  Loader2,
  Plus,
  RefreshCw,
  CheckCircle2,
  X,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Alert } from '@/components/ui/Alert';
import { ProviderSelect } from '@/components/llm/ProviderSelect';
import { ModelSelect } from '@/components/llm/ModelSelect';
import '@/styles/wf-agents.css'; // chrome: .dhead/.dnewbtn
import '@/styles/wf-settings.css';

// Status → tom do wireframe §10 (.llm-st .st-ok/.st-rl/.st-bad; sem tom = cinza).
const CONNECTION_STATUS_MAP: Record<ConnectionStatus, { tone: 'st-ok' | 'st-rl' | 'st-bad' | ''; label: string }> = {
  connected: { tone: 'st-ok', label: 'Conectada' },
  rate_limited_session: { tone: 'st-rl', label: 'Rate limit' },
  rate_limited_weekly: { tone: 'st-bad', label: 'Limite semanal estourado' },
  expiring: { tone: 'st-rl', label: 'Expirando' },
  expired: { tone: 'st-bad', label: 'Expirada' },
  unknown: { tone: '', label: 'Desconhecido' },
};

const PROVIDER_DISPLAY_NAMES: Record<string, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  gemini: 'Google Gemini',
  grok: 'Grok',
};

function getProviderDisplayName(provider: string): string {
  const normalized = provider.trim().toLowerCase();
  if (!normalized) return 'Provedor LLM';
  return PROVIDER_DISPLAY_NAMES[normalized]
    ?? normalized.replace(/(^|[-_])([a-z])/g, (_match, separator: string, letter: string) => (
      `${separator ? ' ' : ''}${letter.toUpperCase()}`
    ));
}

function getAccountPresentation(account: LlmAccountWithStatus): {
  name: string;
  identifier: string;
  providerName: string;
} {
  const providerName = getProviderDisplayName(account.provider);
  const accountLabel = account.label.trim();
  const identifier = account.display_name?.trim();

  return {
    name: accountLabel ? `${providerName} (${accountLabel})` : providerName,
    identifier: identifier || `Conta ${providerName} conectada`,
    providerName,
  };
}

function formatCooldownTime(isoTimestamp: string): string {
  const date = new Date(isoTimestamp);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `ate ${hours}:${minutes}`;
}

// Tom da barra de quota: status não-'allowed' ou >=90% = crítico; >=70% = atenção.
function quotaTone(w: QuotaWindow): 'ok' | 'warn' | 'crit' {
  if (w.status && w.status !== 'allowed') return 'crit';
  const u = w.utilization ?? 0;
  if (u >= 0.9) return 'crit';
  if (u >= 0.7) return 'warn';
  return 'ok';
}

// Reset curto: <24h mostra HH:MM; senão "em Nd". '' se inválido.
function formatResetShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = d.getTime() - Date.now();
  if (diffMs <= 0) return 'agora';
  const hours = diffMs / 3_600_000;
  if (hours < 24) {
    const hh = d.getHours().toString().padStart(2, '0');
    const mm = d.getMinutes().toString().padStart(2, '0');
    return `${hh}:${mm}`;
  }
  return `em ${Math.round(hours / 24)}d`;
}

// Idade do snapshot desde captured_at, pra sinalizar frescor. '' se inválido.
function formatAge(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diffMin = Math.max(0, Math.round((Date.now() - t) / 60_000));
  if (diffMin < 1) return 'agora há pouco';
  if (diffMin < 60) return `há ${diffMin}min`;
  const h = Math.round(diffMin / 60);
  if (h < 24) return `há ${h}h`;
  return `há ${Math.round(h / 24)}d`;
}

/**
 * Barras de quota Sessão (5h) e Semanal (7d). Faixa full-width abaixo do card.
 * Renderiza só as janelas existentes — conta API-key / sem captura não mostra
 * nada (degrada graceful). utilization é fração 0..1; capturedAt indica frescor.
 */
function QuotaBars({ windows, capturedAt }: { windows: QuotaWindow[]; capturedAt?: string | null }) {
  const age = capturedAt ? formatAge(capturedAt) : '';
  return (
    <div className="llm-quota">
      {windows.map((w) => {
        // Clamp defensivo [0,100]: dado externo; mantém a barra e o % coerentes.
        const pct = w.utilization != null ? Math.min(100, Math.max(0, Math.round(w.utilization * 100))) : null;
        const tone = quotaTone(w);
        // role=progressbar exige valor; sem utilization a barra é decorativa.
        const barAria =
          pct != null
            ? ({ role: 'progressbar' as const, 'aria-valuenow': pct, 'aria-valuemin': 0, 'aria-valuemax': 100, 'aria-label': w.label })
            : ({ 'aria-hidden': true } as const);
        return (
          <div key={w.key} className="llm-qrow" title={w.status ? `status: ${w.status}` : undefined}>
            <span className="llm-qlabel">{w.label}</span>
            <span className="llm-qbar" {...barAria}>
              <span className={`llm-qfill tone-${tone}`} style={{ width: `${pct ?? 0}%` }} />
            </span>
            <span className="llm-qpct">{pct != null ? `${pct}%` : '—'}</span>
            {w.resets_at && <span className="llm-qreset">reseta {formatResetShort(w.resets_at)}</span>}
          </div>
        );
      })}
      {age && <span className="llm-qage">atualizado {age}</span>}
    </div>
  );
}

export function LlmAccountsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState<LlmAccountWithStatus[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const [reconnecting] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // LLM status (richer info). llmModel não é mais lido no JSX porque o
  // Select hardcoded virou <ModelSelect> dinâmico (PR #27); mantemos o
  // setter pra compatibilidade com fetchLlmStatus + handleModelChange.
  const [, setLlmModel] = useState('');
  const [savingModel, setSavingModel] = useState(false);
  const [savingFallbackModel, setSavingFallbackModel] = useState<string | null>(null);

  const [refreshingQuota, setRefreshingQuota] = useState(false);

  const fetchAccounts = useCallback(async () => {
    try {
      const data = await api.listLlmAccountsWithStatus();
      setAccounts(data);
    } catch { /* */ }
    finally { setLoadingAccounts(false); }
  }, []);

  // Botão "Atualizar limites": UMA chamada GET ?refresh=1 que mede a quota 5h/7d
  // de todas as contas (modelo real) e já devolve a lista atualizada. Reusa a rota
  // de listar (que o navegador conhece) em vez de um POST novo — sem 404 por cache.
  // Degradação graciosa: se a remedição falhar, mostra o último estado conhecido —
  // o usuário NUNCA vê um erro cru.
  const handleRefreshQuota = useCallback(async () => {
    setRefreshingQuota(true);
    try {
      const updated = await api.listLlmAccountsWithStatus({ refresh: true });
      setAccounts(updated);
      setFeedback({ type: 'success', message: 'Limites atualizados.' });
    } catch {
      try {
        const fallback = await api.listLlmAccountsWithStatus();
        setAccounts(fallback);
        setFeedback({ type: 'success', message: 'Limites atualizados (alguns podem refletir a última leitura).' });
      } catch {
        setFeedback({ type: 'error', message: 'Não consegui atualizar agora. Tente de novo em instantes.' });
      }
    } finally {
      setRefreshingQuota(false);
    }
  }, []);

  const fetchLlmStatus = useCallback(async () => {
    try {
      const status = await api.llmStatus();
      setLlmModel(status.model ?? '');
    } catch { /* */ }
  }, []);

  // LLM-04 — Detect OAuth callback (?llm=connected | ?llm=error&reason=...)
  useEffect(() => {
    const llmStatus = searchParams.get('llm');
    const reason = searchParams.get('reason');
    if (llmStatus === 'connected') {
      setFeedback({ type: 'success', message: 'Conta LLM conectada com sucesso.' });
      setSearchParams({}, { replace: true });
      fetchAccounts();
      fetchLlmStatus();
    } else if (llmStatus === 'error') {
      setFeedback({ type: 'error', message: `Erro ao conectar: ${reason ?? 'desconhecido'}. Tente novamente.` });
      setSearchParams({}, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    fetchAccounts();
    fetchLlmStatus();
  }, [fetchAccounts, fetchLlmStatus]);

  // New connection modal state (OAuth Anthropic)
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [connectOauthState, setConnectOauthState] = useState('');
  const [connectWaitingForCode, setConnectWaitingForCode] = useState(false);
  const [connectCode, setConnectCode] = useState('');
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectError, setConnectError] = useState('');

  // DEM-04.5 — OAuth OpenAI status
  const [openaiOAuthConfigured, setOpenaiOAuthConfigured] = useState<boolean | null>(null);
  useEffect(() => {
    api.openaiOAuthStatus()
      .then((s) => setOpenaiOAuthConfigured(s.configured))
      .catch(() => setOpenaiOAuthConfigured(false));
  }, []);

  // DEM-2170 — OAuth Grok/xAI status (device code flow). O provider vem do
  // catálogo backend-driven; este status governa se o método OAuth pode iniciar.
  const [grokOAuthConfigured, setGrokOAuthConfigured] = useState<boolean | null>(null);
  useEffect(() => {
    api.grokOAuthStatus()
      .then((s) => setGrokOAuthConfigured(s.configured))
      .catch(() => setGrokOAuthConfigured(false));
  }, []);

  // ─── Modal único "Conectar LLM" — orquestra os fluxos ────────────────
  type ConnectStep = 'choose' | 'apikey_form';
  const [llmModalOpen, setLlmModalOpen] = useState(false);
  const [llmStep, setLlmStep] = useState<ConnectStep>('choose');
  // DEM-2170 — 'grok' entra como provider OAuth-only (device flow). A UI de API
  // key do Grok permanece proibida por ADR-2026-07-18-OAUTH-FIRST.
  const [llmProvider, setLlmProvider] = useState<'anthropic' | 'openai' | 'grok'>('anthropic');
  const [llmAuthMethod, setLlmAuthMethod] = useState<'oauth' | 'api_key'>('oauth');
  const selectedOAuthUnavailable = llmAuthMethod === 'oauth' && (
    (llmProvider === 'openai' && openaiOAuthConfigured === false)
    || (llmProvider === 'grok' && grokOAuthConfigured === false)
  );

  function openConnectLLM() {
    setLlmStep('choose');
    setLlmProvider('anthropic');
    setLlmAuthMethod('oauth');
    setLlmModalOpen(true);
  }

  function closeConnectLLM() {
    setLlmModalOpen(false);
  }

  // Device code modal state. DEM-2170: o mesmo modal serve OpenAI e Grok — o
  // deviceProvider governa título, textos e o par de rotas usado no poll.
  const [deviceModalOpen, setDeviceModalOpen] = useState(false);
  const [deviceProvider, setDeviceProvider] = useState<'openai' | 'grok'>('openai');
  const [deviceUserCode, setDeviceUserCode] = useState('');
  const [deviceVerificationUrl, setDeviceVerificationUrl] = useState('');
  const [deviceStatus, setDeviceStatus] = useState<'starting' | 'pending' | 'success' | 'expired' | 'error'>('starting');
  const [deviceError, setDeviceError] = useState('');
  const devicePollHandle = useRef<ReturnType<typeof setTimeout> | null>(null);

  function stopDevicePolling() {
    if (devicePollHandle.current) {
      clearTimeout(devicePollHandle.current);
      devicePollHandle.current = null;
    }
  }

  // Mensagem legível pra códigos de erro do device flow (DEM-2170 AC5). Cobre os
  // códigos que o backend do Grok retorna (device_start_failed, expired_token,
  // access_denied) + os do OpenAI, com fallback pro texto cru.
  function humanizeDeviceError(code: string): string {
    const map: Record<string, string> = {
      device_start_failed: 'Não consegui iniciar a autorização. Tente de novo em instantes.',
      expired_token: 'O código expirou antes da autorização. Gere um novo código.',
      access_denied: 'A autorização foi negada. Tente novamente e autorize o acesso.',
      state_invalid_or_expired: 'A sessão de autorização expirou. Gere um novo código.',
      state_expired: 'A sessão de autorização expirou. Gere um novo código.',
      slow_down: 'Aguardando autorização…',
    };
    return map[code] ?? code;
  }

  /**
   * Device code flow genérico (OpenAI/Grok). DEM-2170: extraído pra reuso — o par
   * de rotas start/poll é escolhido pelo provider. Respeita o interval retornado
   * pelo backend (mín. 3s) e traduz os códigos de erro pra mensagem legível.
   */
  async function runDeviceFlow(
    provider: 'openai' | 'grok',
    start: () => Promise<{ state: string; user_code: string; verification_url: string; interval: number }>,
    poll: (state: string) => Promise<{ status: 'pending' | 'success' | 'expired' | 'error'; account_id?: string; error?: string }>,
    startErrorMsg: string,
  ) {
    setDeviceProvider(provider);
    setDeviceModalOpen(true);
    setDeviceStatus('starting');
    setDeviceError('');
    setDeviceUserCode('');
    setDeviceVerificationUrl('');
    try {
      const started = await start();
      setDeviceUserCode(started.user_code);
      setDeviceVerificationUrl(started.verification_url);
      setDeviceStatus('pending');
      const intervalMs = Math.max(started.interval, 3) * 1000;
      const doPoll = async () => {
        try {
          const result = await poll(started.state);
          if (result.status === 'success') {
            setDeviceStatus('success');
            stopDevicePolling();
            await fetchAccounts();
            setTimeout(() => setDeviceModalOpen(false), 1500);
          } else if (result.status === 'expired') {
            setDeviceStatus('expired');
            stopDevicePolling();
          } else if (result.status === 'error') {
            setDeviceStatus('error');
            setDeviceError(humanizeDeviceError(result.error ?? 'erro desconhecido'));
            stopDevicePolling();
          } else {
            devicePollHandle.current = setTimeout(doPoll, intervalMs);
          }
        } catch (err) {
          setDeviceStatus('error');
          setDeviceError(err instanceof Error ? err.message : 'Erro de rede');
          stopDevicePolling();
        }
      };
      devicePollHandle.current = setTimeout(doPoll, intervalMs);
    } catch (err) {
      setDeviceStatus('error');
      setDeviceError(err instanceof Error ? err.message : startErrorMsg);
    }
  }

  async function handleOpenAIOAuthConnect() {
    await runDeviceFlow(
      'openai',
      () => api.openaiDeviceStart(),
      (state) => api.openaiDevicePoll(state),
      'Erro ao iniciar Sign in with ChatGPT',
    );
  }

  // DEM-2170 — conecta a conta Grok via device code flow da xAI. Espelha o fluxo
  // OpenAI: start → user_code + link accounts.x.ai → poll respeitando interval →
  // em success a conta Grok (OAuth) entra na lista.
  async function handleGrokOAuthConnect() {
    await runDeviceFlow(
      'grok',
      () => api.grokDeviceStart(),
      (state) => api.grokDevicePoll(state),
      humanizeDeviceError('device_start_failed'),
    );
  }

  function closeDeviceModal() {
    stopDevicePolling();
    setDeviceModalOpen(false);
  }

  // Cleanup polling quando componente desmonta
  useEffect(() => () => stopDevicePolling(), []);

  // DEM-08 multi-provider — form de conexão via API Key (Anthropic ou OpenAI)
  const [apiKeyProvider, setApiKeyProvider] = useState<string>('openai');
  const [apiKeyModel, setApiKeyModel] = useState<string>('');
  const [apiKeyValue, setApiKeyValue] = useState<string>('');
  const [apiKeyDisplayName, setApiKeyDisplayName] = useState<string>('');
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [apiKeyError, setApiKeyError] = useState<string>('');

  /** Despacha o fluxo escolhido no modal único. Anthropic OAuth e OpenAI device
   *  delegam pros handlers existentes (não regridem). API Key vira step inline. */
  function proceedConnectLLM() {
    if (llmProvider === 'anthropic' && llmAuthMethod === 'oauth') {
      setLlmModalOpen(false);
      handleOAuthConnect();
      return;
    }
    if (llmProvider === 'openai' && llmAuthMethod === 'oauth') {
      setLlmModalOpen(false);
      handleOpenAIOAuthConnect();
      return;
    }
    // DEM-2170 — Grok é OAuth-only (device flow). API key permanece backend-only
    // (ADR-2026-07-18-OAUTH-FIRST), então não há branch api_key pra grok.
    if (llmProvider === 'grok' && llmAuthMethod === 'oauth') {
      setLlmModalOpen(false);
      handleGrokOAuthConnect();
      return;
    }
    if (llmAuthMethod === 'api_key') {
      // Pré-popula form API Key dentro do mesmo modal
      setApiKeyProvider(llmProvider);
      setApiKeyModel('');
      setApiKeyValue('');
      setApiKeyDisplayName('');
      setApiKeyError('');
      setLlmStep('apikey_form');
      return;
    }
  }

  async function handleApiKeyConnect() {
    if (!apiKeyValue.trim()) {
      setApiKeyError('Cole a API key.');
      return;
    }
    if (!apiKeyProvider) {
      setApiKeyError('Escolha um provider.');
      return;
    }
    setApiKeyLoading(true);
    setApiKeyError('');
    try {
      await api.connectLlmAccountApiKey({
        provider: apiKeyProvider as 'anthropic' | 'openai',
        api_key: apiKeyValue.trim(),
        ...(apiKeyModel ? { model_id: apiKeyModel } : {}),
        ...(apiKeyDisplayName ? { display_name: apiKeyDisplayName.trim() } : {}),
        label: 'api_key',
      });
      setLlmModalOpen(false);
      setFeedback({
        type: 'success',
        message: `Conta ${apiKeyProvider} conectada via API Key.`,
      });
      await fetchAccounts();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao conectar';
      setApiKeyError(msg);
    } finally {
      setApiKeyLoading(false);
    }
  }

  function handleOAuthConnect() {
    setConnectModalOpen(true);
    setConnectWaitingForCode(false);
    setConnectCode('');
    setConnectError('');
  }

  async function handleConnectStartOAuth() {
    setConnectLoading(true);
    setConnectError('');
    try {
      sessionStorage.setItem('raios_oauth_origin', 'settings');
      const { redirectUrl, state } = await api.llmAuthorize();
      setConnectOauthState(state);
      window.open(redirectUrl, '_blank');
      setConnectWaitingForCode(true);
    } catch (err) {
      setConnectError(err instanceof Error ? err.message : 'Erro ao iniciar OAuth');
    } finally {
      setConnectLoading(false);
    }
  }

  async function handleConnectExchangeCode() {
    const raw = connectCode.trim();
    if (!raw) { setConnectError('Cole o codigo'); return; }

    let code = raw;
    let state = connectOauthState;
    if (raw.includes('#')) {
      const parts = raw.split('#');
      code = parts[0]!;
      state = parts[1] || connectOauthState;
    }

    setConnectLoading(true);
    setConnectError('');
    try {
      await api.llmExchange(code, state);
      setFeedback({ type: 'success', message: 'Nova conta conectada com sucesso!' });
      setConnectModalOpen(false);
      const updated = await api.listLlmAccountsWithStatus();
      setAccounts(updated);
      fetchLlmStatus();
    } catch (err) {
      setConnectError(err instanceof Error ? err.message : 'Codigo invalido ou expirado. Tente novamente.');
    } finally {
      setConnectLoading(false);
    }
  }

  // Reconnect modal state
  const [reconnectModalAccountId, setReconnectModalAccountId] = useState<string | null>(null);
  const [reconnectOauthState, setReconnectOauthState] = useState('');
  const [reconnectWaitingForCode, setReconnectWaitingForCode] = useState(false);
  const [reconnectCode, setReconnectCode] = useState('');
  const [reconnectLoading, setReconnectLoading] = useState(false);
  const [reconnectError, setReconnectError] = useState('');

  async function handleReconnect(accountId: string) {
    // Bifurca pelo provider+auth_method da conta. Antes era hardcoded
    // pra Anthropic OAuth — bug: reconectar conta OpenAI ia pro Claude.
    const account = accounts.find((a) => a.id === accountId);
    if (!account) return;

    if ((account.provider === 'openai' || account.provider === 'grok') && account.auth_method === 'oauth') {
      // Device flow com reconnectAccountId pra atualizar conta existente.
      // DEM-2170: mesmo caminho reusado pro Grok (par de rotas por provider).
      const provider = account.provider as 'openai' | 'grok';
      await runDeviceFlow(
        provider,
        () =>
          provider === 'grok'
            ? api.grokDeviceStart(accountId)
            : api.openaiDeviceStart(accountId),
        (state) =>
          provider === 'grok' ? api.grokDevicePoll(state) : api.openaiDevicePoll(state),
        'device_start_failed',
      );
      return;
    }

    if (account.auth_method === 'api_key') {
      // Re-input de API key via modal unificado no step apikey_form
      setApiKeyProvider(account.provider);
      setApiKeyModel(account.model_id ?? '');
      setApiKeyValue('');
      setApiKeyDisplayName(account.display_name ?? '');
      setApiKeyError('');
      setLlmProvider(account.provider as 'anthropic' | 'openai');
      setLlmAuthMethod('api_key');
      setLlmStep('apikey_form');
      setLlmModalOpen(true);
      return;
    }

    // Default: Anthropic OAuth flow (modal existing)
    setReconnectModalAccountId(accountId);
    setReconnectWaitingForCode(false);
    setReconnectCode('');
    setReconnectError('');
  }

  async function handleReconnectStartOAuth() {
    if (!reconnectModalAccountId) return;
    setReconnectLoading(true);
    setReconnectError('');
    try {
      const { redirectUrl, state } = await api.llmAuthorize(reconnectModalAccountId);
      setReconnectOauthState(state);
      window.open(redirectUrl, '_blank');
      setReconnectWaitingForCode(true);
    } catch (err) {
      setReconnectError(err instanceof Error ? err.message : 'Erro ao iniciar OAuth');
    } finally {
      setReconnectLoading(false);
    }
  }

  async function handleReconnectExchangeCode() {
    const raw = reconnectCode.trim();
    if (!raw) { setReconnectError('Cole o codigo'); return; }

    let code = raw;
    let state = reconnectOauthState;
    if (raw.includes('#')) {
      const parts = raw.split('#');
      code = parts[0]!;
      state = parts[1] || reconnectOauthState;
    }

    setReconnectLoading(true);
    setReconnectError('');
    try {
      await api.llmExchange(code, state);
      setFeedback({ type: 'success', message: 'Conta reconectada com sucesso!' });
      setReconnectModalAccountId(null);
      // Refresh accounts list
      const updated = await api.listLlmAccountsWithStatus();
      setAccounts(updated);
    } catch (err) {
      setReconnectError(err instanceof Error ? err.message : 'Codigo invalido. Tente novamente.');
    } finally {
      setReconnectLoading(false);
    }
  }

  async function handleModelChange(accountId: string, newModel: string) {
    setSavingModel(true);
    try {
      await api.updateLlmAccountModel(accountId, newModel);
      setLlmModel(newModel);
      // Refetcha lista pra refletir o novo model_id em accounts[].
      await fetchAccounts();
      setFeedback({ type: 'success', message: `Modelo alterado para ${newModel}.` });
    } catch (err) {
      setFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Erro ao alterar modelo.' });
    } finally {
      setSavingModel(false);
    }
  }

  async function handleFallbackModelChange(accountId: string, fallbackModelId: string) {
    setSavingFallbackModel(accountId);
    try {
      // String vazia vem somente da opção explícita “Sem fallback” e persiste null.
      await api.updateLlmAccountFallbackModel(accountId, fallbackModelId || null);
      await fetchAccounts();
      setFeedback({
        type: 'success',
        message: fallbackModelId ? `Modelo fallback alterado para ${fallbackModelId}.` : 'Modelo fallback removido.',
      });
    } catch (err) {
      setFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Erro ao salvar modelo fallback.' });
    } finally {
      setSavingFallbackModel(null);
    }
  }

  async function handleDisconnect(accountId: string) {
    setDisconnecting(accountId);
    try {
      await api.disconnectLlmAccount(accountId);
      setAccounts((prev) => prev.filter((a) => a.id !== accountId));
      setFeedback({ type: 'success', message: 'Conta desconectada.' });
      fetchLlmStatus();
    } catch (err) {
      setFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Erro ao desconectar.' });
    } finally {
      setDisconnecting(null);
    }
  }

  return (
    <div>
      <PageHeader
        title="Contas LLM"
        description="Contas de provedores conectadas ao seu RAIOS. O seletor de modelo define o padrão da conta; a lixeira remove."
      />

      {/* Header de ação (wireframe §10 .dhead com .dnewbtn). Os dois botões ficam
          AGRUPADOS à direita, na ordem [Atualizar limites] [Conectar LLM]. O
          wrapper com margin-left:auto encosta à direita; os botões anulam o
          margin-left:auto da classe .dnewbtn pra não se separarem. */}
      <div className="dhead" style={{ marginTop: 14 }}>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="dnewbtn"
            onClick={handleRefreshQuota}
            disabled={refreshingQuota}
            title="Re-medir agora os limites de sessão (5h) e semanal (7d) de todas as contas"
            style={{ marginLeft: 0, background: 'transparent', border: '1px solid var(--wf-border)', color: 'var(--wf-text2)' }}
          >
            {refreshingQuota ? (
              <Loader2 className="ic animate-spin" aria-hidden />
            ) : (
              <RefreshCw className="ic" aria-hidden />
            )}
            {refreshingQuota ? 'Atualizando…' : 'Atualizar limites'}
          </button>
          <button type="button" className="dnewbtn" onClick={openConnectLLM} style={{ marginLeft: 0 }}>
            <Plus className="ic" aria-hidden />
            Conectar LLM
          </button>
        </div>
      </div>

      {feedback && (
        <Alert
          variant={feedback.type === 'success' ? 'success' : 'destructive'}
          className="mt-4"
          dismissible
          onDismiss={() => setFeedback(null)}
        >
          {feedback.message}
        </Alert>
      )}

      {/* Lista de contas (wireframe §10 .llm-list/.llm-row) */}
      <div className="llm-list">
        {loadingAccounts ? (
          <>
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-20 rounded-xl" />
            ))}
          </>
        ) : accounts.length === 0 ? (
          <Card className="p-6">
            <EmptyState
              icon={Cpu}
              title="Nenhuma conta LLM conectada"
              description="Conecte sua conta Claude para usar o Companion e os agentes."
              action={{ label: 'Conectar LLM', onClick: openConnectLLM }}
            />
          </Card>
        ) : (
          accounts.map((account) => {
            const statusConfig = CONNECTION_STATUS_MAP[account.connection_status] ?? CONNECTION_STATUS_MAP.unknown;
            const needsReconnect = account.connection_status === 'expired' || account.connection_status === 'expiring' || account.connection_status === 'unknown';
            const resilienceEligible = account.is_active && account.connection_status === 'connected';
            const accountPresentation = getAccountPresentation(account);

            return (
              <div key={account.id} className="llm-row">
                <div className="llm-ic">
                  <Cpu className="ic" aria-hidden />
                </div>
                <div className="llm-main">
                  <div className="llm-name">{accountPresentation.name}</div>
                  <div className="llm-identity">{accountPresentation.identifier}</div>
                  <div className="llm-badges">
                    <span className="llm-bdg prov">{accountPresentation.providerName}</span>
                    {account.auth_method && (
                      <span className="llm-bdg auth">
                        {account.auth_method === 'oauth' ? 'OAuth' : 'API Key'}
                      </span>
                    )}
                    {account.model_id && <span className="llm-bdg mdl">{account.model_id}</span>}
                    {account.current_sessions > 0 && (
                      <span className="llm-bdg">
                        {account.current_sessions}{' '}
                        {account.current_sessions === 1 ? 'sessão ativa' : 'sessões ativas'}
                      </span>
                    )}
                    <span className={`llm-bdg ${resilienceEligible ? 'st-ok' : 'st-bad'}`}>
                      {resilienceEligible ? 'Elegível para fallback' : 'Indisponível para fallback'}
                    </span>
                  </div>
                </div>
                <div className={`llm-st ${statusConfig.tone}`}>
                  <span className="dot" />
                  {statusConfig.label}
                  {account.connection_status === 'rate_limited_session' && account.cooldown_until && (
                    <span style={{ fontSize: '.66rem' }}>
                      {formatCooldownTime(account.cooldown_until)}
                    </span>
                  )}
                </div>
                {needsReconnect && (
                  <button
                    type="button"
                    className="wf-btn wf-btn-primary"
                    style={{ padding: '6px 12px', fontSize: '.76rem' }}
                    onClick={() => handleReconnect(account.id)}
                    disabled={reconnecting === account.id}
                    aria-label="Reconectar conta"
                  >
                    {reconnecting === account.id ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <RefreshCw aria-hidden />
                    )}
                    Reconectar
                  </button>
                )}
                <div className="llm-model">
                  {/* Filtra modelos pelo método de auth da conta:
                      api_key → só 'active' (esconde modelos OAuth-only)
                      oauth → 'all' (inclui available_via_oauth) */}
                  <ModelSelect
                    label="Modelo principal"
                    provider={account.provider}
                    statusFilter={account.auth_method === 'api_key' ? 'active' : 'all'}
                    value={account.model_id || ''}
                    onChange={(v) => handleModelChange(account.id, v)}
                    placeholder={savingModel ? 'Salvando…' : 'Escolha o modelo…'}
                    disabled={savingModel}
                  />
                </div>
                <div className="llm-model">
                  <ModelSelect
                    label="Modelo fallback"
                    provider={account.provider}
                    statusFilter={account.auth_method === 'api_key' ? 'active' : 'all'}
                    value={account.fallback_model_id ?? ''}
                    onChange={(value) => handleFallbackModelChange(account.id, value)}
                    placeholder={savingFallbackModel === account.id ? 'Salvando…' : 'Escolha o fallback…'}
                    emptyOptionLabel="Sem fallback"
                    disabled={savingFallbackModel === account.id}
                  />
                </div>
                <Link
                  to={`/llm-resilience?account_id=${encodeURIComponent(account.id)}`}
                  className="text-xs font-medium text-raios-accent-text underline-offset-2 hover:underline"
                >
                  Ver attempts recentes
                </Link>
                <button
                  type="button"
                  className="llm-del"
                  title="Remover conta"
                  onClick={() => handleDisconnect(account.id)}
                  disabled={disconnecting === account.id}
                  aria-label="Desconectar conta"
                >
                  {disconnecting === account.id ? (
                    <Loader2 className="ic animate-spin" aria-hidden />
                  ) : (
                    <Trash2 className="ic" aria-hidden />
                  )}
                </button>
                {account.quota_windows && account.quota_windows.length > 0 ? (
                  <QuotaBars windows={account.quota_windows} capturedAt={account.quota_captured_at} />
                ) : account.quota_probe_degraded ? (
                  <div
                    className="llm-quota llm-quota-degraded"
                    role="status"
                    title={
                      account.quota_probe_degraded.top_level_keys?.length
                        ? `keys: ${account.quota_probe_degraded.top_level_keys.join(', ')}`
                        : undefined
                    }
                  >
                    <span className="llm-qdegrade">
                      ⚠ {account.quota_probe_degraded.message}
                    </span>
                    {account.quota_captured_at && (
                      <span className="llm-qage">atualizado {formatAge(account.quota_captured_at)}</span>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </div>

      {/* Reconnect Modal */}
      {reconnectModalAccountId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <Card className="w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-semibold text-raios-text">Reconectar Conta Claude</h3>

            {reconnectError && (
              <p className="text-xs text-raios-danger-text bg-raios-danger/10 rounded px-3 py-2">{reconnectError}</p>
            )}

            {!reconnectWaitingForCode ? (
              <div className="space-y-4">
                <p className="text-sm text-raios-text-secondary">
                  Clique abaixo para abrir a pagina de autorizacao da Anthropic. Apos autorizar, copie o codigo que aparecera na tela.
                </p>
                <div className="flex gap-2">
                  <Button variant="brand" onClick={handleReconnectStartOAuth} loading={reconnectLoading} className="flex-1">
                    Abrir Autorizacao
                  </Button>
                  <Button variant="outline" onClick={() => setReconnectModalAccountId(null)}>
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-raios-text-secondary">
                  Uma aba foi aberta. Apos autorizar, copie o codigo que aparece na tela e cole abaixo.
                </p>
                <Input
                  label="Codigo de autorizacao"
                  value={reconnectCode}
                  onChange={(e) => setReconnectCode(e.target.value)}
                  placeholder="Ex: AbCdEfGh...#1234abcd..."
                  helperText="Cole o codigo completo (incluindo # e tudo depois)."
                />
                <div className="flex gap-2">
                  <Button variant="brand" onClick={handleReconnectExchangeCode} loading={reconnectLoading} disabled={!reconnectCode.trim()} className="flex-1">
                    Confirmar
                  </Button>
                  <Button variant="outline" onClick={() => { setReconnectWaitingForCode(false); setReconnectCode(''); }}>
                    Voltar
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* New Connection Modal (OAuth Anthropic) */}
      {connectModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <Card className="w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-semibold text-raios-text">Conectar Nova Conta Claude</h3>

            {connectError && (
              <p className="text-xs text-raios-danger-text bg-raios-danger/10 rounded px-3 py-2">{connectError}</p>
            )}

            {!connectWaitingForCode ? (
              <div className="space-y-4">
                <p className="text-sm text-raios-text-secondary">
                  Clique abaixo para abrir a pagina de autorizacao da Anthropic. Apos autorizar, copie o codigo que aparecera na tela.
                </p>
                <div className="flex gap-2">
                  <Button variant="brand" onClick={handleConnectStartOAuth} loading={connectLoading} className="flex-1">
                    Abrir Autorizacao
                  </Button>
                  <Button variant="outline" onClick={() => setConnectModalOpen(false)}>
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-raios-text-secondary">
                  Uma aba foi aberta. Apos autorizar, copie o codigo que aparece na tela e cole abaixo.
                </p>
                <Input
                  label="Codigo de autorizacao"
                  value={connectCode}
                  onChange={(e) => setConnectCode(e.target.value)}
                  placeholder="Ex: AbCdEfGh...#1234abcd..."
                  helperText="Cole o codigo completo (incluindo # e tudo depois)."
                />
                <div className="flex gap-2">
                  <Button variant="brand" onClick={handleConnectExchangeCode} loading={connectLoading} disabled={!connectCode.trim()} className="flex-1">
                    Confirmar
                  </Button>
                  <Button variant="outline" onClick={() => { setConnectWaitingForCode(false); setConnectCode(''); }}>
                    Voltar
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Modal único "Conectar LLM" — orquestra Anthropic+OAuth/API Key e OpenAI+OAuth/API Key */}
      {llmModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <Card className="w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-raios-text">
                {llmStep === 'choose' ? 'Conectar LLM' : `Conectar ${llmProvider} via API Key`}
              </h3>
              <button type="button" onClick={closeConnectLLM} className="text-raios-muted hover:text-raios-text" aria-label="Fechar">
                <X className="h-4 w-4" />
              </button>
            </div>

            {llmStep === 'choose' && (
              <div className="space-y-4">
                <ProviderSelect
                  value={llmProvider}
                  onChange={(v) => {
                    const p = (v as 'anthropic' | 'openai' | 'grok');
                    setLlmProvider(p);
                    // Reseta auth_method pra um suportado pelo provider escolhido.
                    setLlmAuthMethod('oauth');
                  }}
                  label="Provider"
                />

                <div>
                  <p className="text-sm font-medium text-raios-text mb-2">Método de autenticação</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setLlmAuthMethod('oauth')}
                      aria-pressed={llmAuthMethod === 'oauth'}
                      disabled={selectedOAuthUnavailable}
                      className={`p-3 rounded-lg border-2 text-left transition-colors ${
                        llmAuthMethod === 'oauth'
                          ? 'border-raios-accent bg-raios-accent/10'
                          : 'border-raios-border bg-raios-surface hover:border-raios-accent/50'
                      } ${selectedOAuthUnavailable ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <p className="text-sm font-semibold">OAuth</p>
                      <p className="text-[11px] text-raios-muted mt-1">
                        {llmProvider === 'anthropic'
                          ? 'Login via Claude.ai. Tokens auto-renew.'
                          : llmProvider === 'grok'
                          ? 'Login via device code em accounts.x.ai.'
                          : 'Sign in with ChatGPT (device code).'}
                      </p>
                    </button>
                    {/* DEM-2170 — Grok NÃO expõe API Key na UI (ADR-2026-07-18-
                        OAUTH-FIRST): o endpoint api-key permanece backend-only.
                        Pra grok, este card fica oculto. */}
                    {llmProvider !== 'grok' && (
                      <button
                        type="button"
                        onClick={() => setLlmAuthMethod('api_key')}
                        aria-pressed={llmAuthMethod === 'api_key'}
                        className={`p-3 rounded-lg border-2 text-left transition-colors ${
                          llmAuthMethod === 'api_key'
                            ? 'border-raios-accent bg-raios-accent/10'
                            : 'border-raios-border bg-raios-surface hover:border-raios-accent/50'
                        }`}
                      >
                        <p className="text-sm font-semibold">API Key</p>
                        <p className="text-[11px] text-raios-muted mt-1">
                          Cole uma chave {llmProvider === 'openai' ? 'sk-…' : 'sk-ant-…'} (estável, sem expiração).
                        </p>
                      </button>
                    )}
                  </div>
                  {selectedOAuthUnavailable && (
                    <p className="mt-2 text-xs text-raios-danger-text" role="status">
                      OAuth indisponível para este provider neste ambiente.
                    </p>
                  )}
                </div>

                <div className="flex gap-2 pt-2">
                  <Button variant="brand" onClick={proceedConnectLLM} disabled={selectedOAuthUnavailable} className="flex-1">
                    Continuar
                  </Button>
                  <Button variant="outline" onClick={closeConnectLLM}>
                    Cancelar
                  </Button>
                </div>
              </div>
            )}

            {llmStep === 'apikey_form' && (
              <div className="space-y-4">
                {apiKeyError && (
                  <p className="text-xs text-raios-danger-text bg-raios-danger/10 rounded px-3 py-2">
                    {apiKeyError}
                  </p>
                )}

                <ModelSelect
                  provider={apiKeyProvider}
                  statusFilter="active"
                  value={apiKeyModel}
                  onChange={(v) => setApiKeyModel(v)}
                  label="Modelo default (opcional)"
                  placeholder="Deixar em branco usa o default do provider"
                />

                <Input
                  label="API Key"
                  type="password"
                  placeholder={apiKeyProvider === 'openai' ? 'sk-…' : 'sk-ant-…'}
                  value={apiKeyValue}
                  onChange={(e) => setApiKeyValue(e.target.value)}
                  autoComplete="off"
                />

                <Input
                  label="Nome (opcional)"
                  placeholder={`${apiKeyProvider} principal`}
                  value={apiKeyDisplayName}
                  onChange={(e) => setApiKeyDisplayName(e.target.value)}
                />

                <div className="flex gap-2 pt-2">
                  <Button variant="brand" onClick={handleApiKeyConnect} loading={apiKeyLoading} className="flex-1">
                    Conectar
                  </Button>
                  <Button variant="outline" onClick={() => setLlmStep('choose')} disabled={apiKeyLoading}>
                    Voltar
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* DEM-04.5 — Modal Device Code (Sign in with ChatGPT) */}
      {deviceModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <Card className="w-full max-w-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-raios-text">
                {deviceProvider === 'grok' ? 'Conectar Grok (xAI)' : 'Sign in with ChatGPT'}
              </h3>
              <button
                type="button"
                onClick={closeDeviceModal}
                className="text-raios-muted hover:text-raios-text"
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {deviceStatus === 'starting' && (
              <div className="flex items-center gap-2 text-sm text-raios-text-secondary">
                <Loader2 className="h-4 w-4 animate-spin" />
                Preparando código…
              </div>
            )}

            {deviceStatus === 'pending' && deviceUserCode && (
              <div className="space-y-4">
                <ol className="space-y-2 text-sm text-raios-text-secondary">
                  <li>1. Abra <a href={deviceVerificationUrl} target="_blank" rel="noreferrer" className="text-raios-accent-text underline font-medium">{deviceVerificationUrl}</a></li>
                  <li>2. Faça login na sua conta {deviceProvider === 'grok' ? 'X / xAI' : 'ChatGPT'} (se ainda não estiver)</li>
                  <li>3. Cole o código abaixo na página da {deviceProvider === 'grok' ? 'xAI' : 'OpenAI'}</li>
                  <li>4. Autorize o acesso. Esta página atualiza sozinha.</li>
                </ol>

                <div className="bg-raios-surface-alt rounded-lg p-6 text-center space-y-2">
                  <p className="text-xs text-raios-muted uppercase tracking-wide">Seu código</p>
                  <div className="flex items-center justify-center gap-2">
                    <code className="text-3xl font-mono font-bold text-raios-text tracking-widest select-all">
                      {deviceUserCode}
                    </code>
                    <button
                      type="button"
                      onClick={() => navigator.clipboard.writeText(deviceUserCode)}
                      className="p-2 hover:bg-raios-surface rounded text-raios-muted hover:text-raios-text"
                      title="Copiar código"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-[10px] text-raios-muted">expira em 15 minutos</p>
                </div>

                <div className="flex items-center gap-2 text-xs text-raios-muted">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Aguardando autorização na {deviceProvider === 'grok' ? 'xAI' : 'OpenAI'}…
                </div>
              </div>
            )}

            {deviceStatus === 'success' && (
              <div className="space-y-3 text-center py-4">
                <CheckCircle2 className="h-12 w-12 text-raios-success-text mx-auto" />
                <p className="text-sm font-semibold text-raios-text">
                  Conta {deviceProvider === 'grok' ? 'Grok' : 'OpenAI'} conectada!
                </p>
                <p className="text-xs text-raios-muted">Fechando…</p>
              </div>
            )}

            {deviceStatus === 'expired' && (
              <div className="space-y-3">
                <p className="text-sm text-raios-warning-text bg-raios-warning/10 rounded px-3 py-2">
                  Código expirou. Tente de novo.
                </p>
                <Button
                  variant="brand"
                  onClick={deviceProvider === 'grok' ? handleGrokOAuthConnect : handleOpenAIOAuthConnect}
                  className="w-full"
                >
                  Gerar novo código
                </Button>
              </div>
            )}

            {deviceStatus === 'error' && (
              <div className="space-y-3">
                <p className="text-xs text-raios-danger-text bg-raios-danger/10 rounded px-3 py-2">
                  {deviceError}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="brand"
                    onClick={deviceProvider === 'grok' ? handleGrokOAuthConnect : handleOpenAIOAuthConnect}
                    className="flex-1"
                  >
                    Tentar de novo
                  </Button>
                  <Button variant="outline" onClick={closeDeviceModal}>
                    Fechar
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
